import type {
  CarePlan,
  Condition,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import { CARE_PLAN_SORT_ORDER } from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import { BasePlan } from '#src/models/care-plans/BasePlan.ts'
import {
  CLOSED_CARE_PLAN_STATUSES,
  GOAL_CARE_PLAN_CATEGORY,
} from '#src/models/care-plans/constants.ts'
import type { QueryTypes } from '#src/search.ts'

export class GoalCarePlan extends BasePlan {
  @Extension({
    url: CARE_PLAN_SORT_ORDER,
    kind: 'valueDecimal',
  })
  sortOrder?: number

  static searchQuery({
    initialCarePlan,
  }: {
    initialCarePlan?: Reference<CarePlan>
  } = {}): QueryTypes {
    return {
      'part-of': initialCarePlan,
      'status:not': 'entered-in-error',
    }
  }

  static byParticipant(subject: Reference<Patient>): QueryTypes {
    return {
      subject,
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  static queryOpenByParticipant(subject: Reference<Patient>): QueryTypes {
    return {
      ...this.queryAll(),
      subject,
      'status:not': CLOSED_CARE_PLAN_STATUSES,
    }
  }

  static queryByCondition(conditions: Reference<Condition>[]): QueryTypes {
    return {
      category: GOAL_CARE_PLAN_CATEGORY,
      condition: conditions,
      'status:not': 'entered-in-error',
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  static queryAll(): QueryTypes {
    return {
      category: GOAL_CARE_PLAN_CATEGORY,
      'status:not': 'entered-in-error',
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  constructor({
    subject,
    order,
    initialCarePlan,
    title,
    description,
    addresses,
    ...rest
  }: Omit<CarePlan, 'resourceType' | 'intent' | 'status'> & {
    subject: CarePlan['subject']
    order: number
    initialCarePlan: Reference<CarePlan>
    title?: string
    description?: string
    addresses?: Reference<Condition>[]
  }) {
    super({
      title: title ?? 'New Goal',
      subject,
      category: [GOAL_CARE_PLAN_CATEGORY],
      partOf: [initialCarePlan],
      description,
      addresses,
      ...rest,
    })
    this.sortOrder = order
  }

  static isInstance(carePlan: BasePlan): carePlan is BasePlan {
    return hasCodeableConcept(carePlan.categories, GOAL_CARE_PLAN_CATEGORY)
  }
}
